<template>
  <Table
    v-bind="$attrs"
    class="filter-table"
    :busy="isBusy || $attrs.busy"
    :current-page="currentPage"
    :items="items"
    :fields="fields"
    :is-all-selected="isAllSelected"
    :is-indeterminate="isIndeterminate"
    :no-local-sorting="true"
    :page-size="pageSize"
    :total-pages="totalPages"
    :sort-by="orderBy ? orderBy : undefined"
    :sort-desc="order === 'desc'"
    @handleClick="event => $emit('handleClick', event)"
    @pageChanged="onPage"
    @pageSizeChanged="onPageSize"
    @sortChanged="onSort"
    @checkboxChanged="select"
    @selectAll="selectAll"
  />
</template>

<script lang="ts">
import { debounce } from 'lodash-es'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import Table from './Table.vue'

import type { Field } from './fields'

export type { Field }

interface Response {
  count: number
  data: Record<string, any>
}

interface ListCallback {
  (params: Record<string, any>): Promise<Response>
}

@Component({
  components: {
    Table
  }
})
export default class FilterTable extends Vue {
  @Prop({ required: true })
  readonly fields!: Field[]

  @Prop({ required: true })
  readonly callback!: ListCallback

  @Prop({ default: '' })
  readonly filter!: string

  @Prop({ default: 1 })
  private currentPage!: number

  @Prop({ required: false })
  readonly clearSelectedItems!: boolean

  //props for order and orderby, both optional
  @Prop({ default: false })
  readonly order!: string

  @Prop({ default: false })
  readonly orderBy!: string

  @Prop({ default: false })
  readonly refreshSelectedItemsOnFilterUpdate!: boolean

  descending = false
  ordering: string | undefined

  isBusy = false

  items: Record<string, unknown>[] = []
  pageSize = 50
  totalPages = 0

  isAllSelected = false
  isIndeterminate = false
  selectedItems: any = []

  mounted() {
    this.ordering = this.fields.find(e => e?.default_ordering)?.key
    this.fetch()
  }

  async fetch(resetCurrentPage = false) {
    try {
      const params: Record<string, any> = {
        page: resetCurrentPage ? 1 : this.currentPage,
        page_size: this.pageSize
      }

      if (this.filter) {
        params.search = this.filter
      }

      if (this.ordering || (this.orderBy && this.order)) {
        // Construct the appropriate ordering string and then hit the API.
        let field: any | undefined = {}
        let ordering = ''
        if (this.ordering) {
          field = this.fields.find(e => e?.key === this.ordering)
          ordering = `${field?.key}`
          if (field?.lookup) ordering = field.lookup
          else if (field?.parent) ordering = field.parent + '__' + ordering
          if (this.descending) ordering = '-' + ordering
        } else if (this.order && this.orderBy) {
          field = this.fields.find(e => e?.key === this.orderBy)
          ordering = this.order === 'desc' ? `-${field?.key}` : field?.key
        }
        params.ordering = ordering
      }

      this.isBusy = true
      const response = await this.callback(params)
      this.items = response.data.results

      this.items.forEach(item => {
        const isSelected = this.selectedItems.some(
          selectedItem => selectedItem.id === item.id
        )
        this.$set(item, 'checkbox', isSelected)
      })

      this.totalPages = Math.ceil(response.data.count / this.pageSize)
      this.setSelectAll()
    } finally {
      this.isBusy = false
    }
  }

  select(selected, isChecked) {
    if (isChecked) {
      this.selectedItems.push(selected)
    } else {
      this.selectedItems = this.selectedItems.filter(
        item => item.id !== selected.id
      )
    }
    this.setSelectAll()
    this.selectedItemsUpdated()
  }

  setSelectAll() {
    if (this.items.length) {
      this.isAllSelected = !this.items.some(item => item.checkbox === false)
      if (this.isAllSelected) {
        this.isIndeterminate = false
      } else {
        this.isIndeterminate = this.items.some(item => item.checkbox === true)
        this.isAllSelected = this.isIndeterminate
      }
    } else {
      this.isAllSelected = false
      this.isIndeterminate = false
    }
  }

  selectAll(isChecked) {
    this.isAllSelected = isChecked
    this.isIndeterminate = false
    this.items.forEach(item => {
      item.checkbox = isChecked
    })

    if (isChecked) {
      this.selectedItems = [...this.selectedItems, ...this.items]
    } else {
      this.selectedItems = this.selectedItems.filter(
        selectedItem => !this.items.some(item => item.id === selectedItem.id)
      )
    }
    this.selectedItemsUpdated()
  }

  selectedItemsUpdated() {
    return this.$emit('selectedItemsUpdated', this.selectedItems)
  }

  clearAllSelections() {
    this.selectedItems = []
    this.items.forEach(item => {
      this.$set(item, 'checkbox', false)
    })
    this.setSelectAll()
    this.selectedItemsUpdated()
  }

  @Watch('filter')
  debouncedFetch = debounce(async () => {
    await this.$emit('updatedPage', 1)
    await this.fetch()
    if (this.refreshSelectedItemsOnFilterUpdate) {
      this.selectedItemsUpdated()
    }
  }, 700)

  @Watch('clearSelectedItems')
  async clearSelected() {
    if (this.clearSelectedItems) {
      this.selectedItems = this.selectedItems.forEach(
        item => (item.checkbox = false)
      )
      this.selectedItems = []
      this.setSelectAll()
    }
  }

  async onPageSize(pageSize) {
    this.pageSize = pageSize
    await this.$emit('updatedPage', 1)
    //adding this new event and not changing "updatedPage" emit to keep it backwards compatible with other uses of this component.
    await this.$emit('pageSizeChanged', pageSize)
    await this.fetch()
  }

  async onPage(page) {
    await this.$emit('updatedPage', page)
    await this.fetch()
  }

  onSort(event) {
    this.$emit('sortChanged', event)
    this.ordering = event.sortBy
    this.descending = event.sortDesc
    this.fetch()
  }
}
</script>

<style lang="scss">
.filter-table > .table-content-container > table > thead > tr > th {
  vertical-align: middle;
}
</style>
